import { useEffect, useState } from "react";
import clinicAPI from "../../../services/clinicApi";
import {
  formatDateShort,
  membershipEffectivePrice,
} from "../AdminPatients/clientSportDisplay";
import { isMembershipFrozenNow } from "../../../utils/membershipAssignUtils";
import s from "../clinic.module.css";
import { Button, Input, useToast } from "../../ui";
import CancelPendingMembershipButton from "./CancelPendingMembershipButton";

function Stepper({
  value,
  onChange,
  min = 0,
  max = 9999,
  disabled = false,
  ariaLabel = "Количество",
}) {
  const n = Number(value);
  const safe = Number.isFinite(n) ? n : min;

  const set = (next) => {
    const clamped = Math.max(min, Math.min(max, next));
    onChange(String(clamped));
  };

  return (
    <div className={s.membershipStepper}>
      <button
        type="button"
        className={s.membershipStepperBtn}
        aria-label="Минус"
        disabled={disabled || safe <= min}
        onClick={() => set(safe - 1)}
      >
        −
      </button>
      <input
        className={s.membershipStepperInput}
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        step="1"
        value={value}
        aria-label={ariaLabel}
        disabled={disabled}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === "") {
            onChange("");
            return;
          }
          const v = Number(raw);
          if (!Number.isFinite(v)) return;
          onChange(String(Math.max(min, Math.min(max, Math.trunc(v)))));
        }}
      />
      <button
        type="button"
        className={s.membershipStepperBtn}
        aria-label="Плюс"
        disabled={disabled || safe >= max}
        onClick={() => set(safe + 1)}
      >
        +
      </button>
    </div>
  );
}

export default function MembershipAdjustPanel({
  membership,
  onChanged,
  initialOpen = false,
  onRequestClose,
  hideToggle = false,
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(initialOpen);
  const [visitsLeft, setVisitsLeft] = useState(
    String(membership?.visits_remaining ?? 0),
  );
  const [termDays, setTermDays] = useState("7");
  const [startsAt, setStartsAt] = useState(membership?.starts_at || "");
  const [endsAt, setEndsAt] = useState(membership?.ends_at || "");
  const [debtAmount, setDebtAmount] = useState(() => {
    const n = membershipEffectivePrice(membership);
    return Number.isFinite(n) ? String(Math.round(n)) : "";
  });

  useEffect(() => {
    setVisitsLeft(String(membership?.visits_remaining ?? 0));
    setStartsAt(membership?.starts_at || "");
    setEndsAt(membership?.ends_at || "");
    const n = membershipEffectivePrice(membership);
    setDebtAmount(Number.isFinite(n) ? String(Math.round(n)) : "");
    setOpen(Boolean(initialOpen));
  }, [
    membership?.id,
    membership?.visits_remaining,
    membership?.starts_at,
    membership?.ends_at,
    membership?.price,
    membership?.plan?.price,
    membership?.amount_due,
    initialOpen,
  ]);

  if (!membership) return null;

  const frozen = isMembershipFrozenNow(membership);
  const isVisits =
    membership.plan?.plan_type === "visits" ||
    membership.visits_remaining != null;
  const hasEndDate = Boolean(membership.ends_at);
  const currentVisits = membership.visits_remaining ?? 0;
  const visitsDirty =
    visitsLeft !== "" && Number(visitsLeft) !== currentVisits;
  const startDateDirty =
    Boolean(startsAt) && startsAt !== (membership.starts_at || "");
  const dateDirty = Boolean(endsAt) && endsAt !== (membership.ends_at || "");
  const currentPrice = membershipEffectivePrice(membership);
  const debtDirty =
    debtAmount !== "" &&
    Number(String(debtAmount).replace(",", ".")) !== currentPrice;
  const showDebtEdit =
    membership.status === "pending" ||
    membership.status === "active" ||
    membership.status === "frozen";

  const patch = async (payload, successMessage = "Сохранено") => {
    setBusy(true);
    try {
      await clinicAPI.updateClientMembership(membership.id, payload);
      toast.success(successMessage);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    if (hideToggle) return null;
    return (
      <Button
        type="button"
        size="sm"
        variant="secondary"
        className={s.membershipAdjustToggleBtn}
        onClick={() => setOpen(true)}
      >
        Изменить занятия и срок
      </Button>
    );
  }

  const confirmBurn = (label) => {
    if (!hasEndDate && isVisits && currentVisits > 0) {
      return window.confirm(
        `Сейчас ${currentVisits} зан. без срока. ${label} Неиспользованные занятия после этой даты сгорят. Продолжить?`,
      );
    }
    return true;
  };

  const paidTotal = Number(membership.paid_total || 0);

  const saveDebt = async () => {
    const n = Number(String(debtAmount).replace(",", "."));
    if (!Number.isFinite(n) || n < 0) {
      toast.error("Укажите сумму от 0");
      return;
    }
    const next = Math.round(n * 100) / 100;
    if (next === currentPrice) {
      toast.error("Сумма не изменилась");
      return;
    }
    if (paidTotal > 0 && next + 1e-6 < paidTotal) {
      toast.error(
        `Нельзя меньше уже оплаченных ${Math.round(paidTotal)} ₽`,
      );
      return;
    }
    const newDue = Math.max(0, Math.round((next - paidTotal) * 100) / 100);
    if (newDue > 0 && next > currentPrice) {
      if (
        !window.confirm(
          `Уже оплачено ${Math.round(paidTotal)} ₽. После сохранения долг станет ${Math.round(newDue)} ₽. Продолжить?`,
        )
      ) {
        return;
      }
    }
    await patch({ price: next }, `Сумма к оплате: ${Math.round(next)} ₽`);
  };

  const saveVisits = async () => {
    const n = Number(visitsLeft);
    if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) {
      toast.error("Укажите целое число от 0");
      return;
    }
    if (n === currentVisits) {
      toast.error("Количество не изменилось");
      return;
    }
    await patch(
      { visits_remaining: n },
      n > currentVisits
        ? `Добавлено ${n - currentVisits} зан.`
        : `Списано ${currentVisits - n} зан.`,
    );
  };

  const extendTerm = async () => {
    const days = Number(termDays);
    if (!Number.isFinite(days) || !Number.isInteger(days) || days < 1) {
      toast.error("Укажите число дней от 1");
      return;
    }
    if (!confirmBurn(`Срок станет ещё ${days} дн. с сегодня.`)) return;
    await patch(
      { extend_days: days },
      hasEndDate
        ? `Срок продлён на ${days} дн.`
        : `Назначен срок: ещё ${days} дн.`,
    );
    setTermDays("7");
  };

  const saveStartDate = async () => {
    if (!startsAt) {
      toast.error("Укажите дату начала");
      return;
    }
    if (!startDateDirty) {
      toast.error("Дата не изменилась");
      return;
    }
    if (
      endsAt &&
      startsAt > endsAt &&
      !window.confirm(
        "Дата начала позже окончания. Всё равно сохранить?",
      )
    ) {
      return;
    }
    const pendingHint =
      membership.status === "pending"
        ? " У неоплаченного абонемента срок окончания пересчитается от новой даты начала."
        : "";
    if (
      !window.confirm(
        `Начало абонемента: ${formatDateShort(startsAt)}.${pendingHint} Продолжить?`,
      )
    ) {
      return;
    }
    await patch({ starts_at: startsAt }, `Начало с ${formatDateShort(startsAt)}`);
  };

  const saveEndDate = async () => {
    if (!endsAt) {
      toast.error("Укажите дату окончания");
      return;
    }
    if (!dateDirty) {
      toast.error("Дата не изменилась");
      return;
    }
    if (!confirmBurn(`Окончание: ${formatDateShort(endsAt)}.`)) return;
    await patch({ ends_at: endsAt }, `Срок до ${formatDateShort(endsAt)}`);
  };

  return (
    <div
      className={`${s.membershipAdjustForm} ${
        initialOpen ? s.membershipAdjustFormSheet : ""
      }`}
    >
      {showDebtEdit ? (
        <section className={s.membershipAdjustBlock}>
          <header className={s.membershipAdjustHead}>
            <h4 className={s.membershipAdjustBlockTitle}>Сумма к оплате</h4>
            <span className={s.membershipAdjustNow}>
              сейчас {Math.round(currentPrice)} ₽
              {membership.amount_due != null && membership.amount_due > 0
                ? ` · долг ${Math.round(membership.amount_due)} ₽`
                : ""}
              {paidTotal > 0 ? ` · внесено ${Math.round(paidTotal)} ₽` : ""}
            </span>
          </header>
          <div className={s.membershipAdjustControls}>
            <Input
              type="number"
              min={0}
              step={1}
              inputMode="numeric"
              value={debtAmount}
              disabled={busy}
              onChange={(e) => setDebtAmount(e.target.value)}
              aria-label="Сумма к оплате"
            />
            <Button
              type="button"
              size="sm"
              className={s.membershipAdjustActionBtn}
              disabled={busy || !debtDirty}
              onClick={saveDebt}
            >
              Сохранить
            </Button>
          </div>
          <p className={s.membershipAdjustHint}>
            Долг = эта сумма минус уже оплаченное
            {paidTotal > 0 ? ` (${Math.round(paidTotal)} ₽)` : ""}. Если
            поднять сумму — долг снова появится.
          </p>
        </section>
      ) : null}

      {isVisits ? (
        <section className={s.membershipAdjustBlock}>
          <header className={s.membershipAdjustHead}>
            <h4 className={s.membershipAdjustBlockTitle}>Занятия</h4>
            <span className={s.membershipAdjustNow}>сейчас {currentVisits}</span>
          </header>
          <div className={s.membershipAdjustControls}>
            <Stepper
              value={visitsLeft}
              onChange={setVisitsLeft}
              min={0}
              disabled={busy || frozen}
              ariaLabel="Остаток занятий"
            />
            <Button
              type="button"
              size="sm"
              className={s.membershipAdjustActionBtn}
              disabled={busy || frozen || !visitsDirty}
              onClick={saveVisits}
            >
              Сохранить
            </Button>
          </div>
        </section>
      ) : null}

      <section className={s.membershipAdjustBlock}>
        <header className={s.membershipAdjustHead}>
          <h4 className={s.membershipAdjustBlockTitle}>
            {hasEndDate ? "Срок" : "Срок (сейчас без ограничения)"}
          </h4>
          <span className={s.membershipAdjustNow}>
            {membership.starts_at
              ? `с ${formatDateShort(membership.starts_at)}`
              : "без начала"}
            {hasEndDate
              ? ` · до ${formatDateShort(membership.ends_at)}`
              : " · без окончания"}
          </span>
        </header>

        <div className={s.membershipAdjustDateBlock}>
          <label
            className={s.membershipAdjustDateLabel}
            htmlFor={`mem-start-${membership.id}`}
          >
            Дата начала
          </label>
          <div
            className={`${s.membershipAdjustControls} ${s.membershipAdjustDateControls}`}
          >
            <Input
              id={`mem-start-${membership.id}`}
              type="date"
              className={s.membershipAdjustDateInput}
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
              disabled={busy || frozen}
            />
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className={s.membershipAdjustActionBtn}
              disabled={busy || frozen || !startDateDirty}
              onClick={saveStartDate}
            >
              Поставить
            </Button>
          </div>
          <p className={s.membershipAdjustHint}>
            От этой даты занятия списываются с абонемента. Если человек был
            раньше — сдвиньте начало на день тренировки.
          </p>
        </div>

        <div className={s.membershipAdjustControls}>
          <Stepper
            value={termDays}
            onChange={setTermDays}
            min={1}
            disabled={busy || frozen}
            ariaLabel="Дней"
          />
          <Button
            type="button"
            size="sm"
            className={s.membershipAdjustActionBtn}
            disabled={busy || frozen}
            onClick={extendTerm}
          >
            {hasEndDate ? "Продлить" : "Назначить"}
          </Button>
        </div>

        <div className={s.membershipAdjustPresets} role="group" aria-label="Быстрый выбор">
          {[7, 14, 30].map((d) => (
            <button
              key={d}
              type="button"
              className={
                String(d) === String(termDays)
                  ? s.membershipAdjustPresetOn
                  : s.membershipAdjustPreset
              }
              disabled={busy || frozen}
              onClick={() => setTermDays(String(d))}
            >
              {d} дн.
            </button>
          ))}
        </div>

        <div className={s.membershipAdjustDateBlock}>
          <label className={s.membershipAdjustDateLabel} htmlFor={`mem-end-${membership.id}`}>
            Точная дата окончания
          </label>
          <div className={`${s.membershipAdjustControls} ${s.membershipAdjustDateControls}`}>
            <Input
              id={`mem-end-${membership.id}`}
              type="date"
              className={s.membershipAdjustDateInput}
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
              disabled={busy || frozen}
            />
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className={s.membershipAdjustActionBtn}
              disabled={busy || frozen || !dateDirty}
              onClick={saveEndDate}
            >
              Поставить
            </Button>
          </div>
        </div>
      </section>

      <div className={s.membershipAdjustFooter}>
        <button
          type="button"
          className={s.membershipAdjustToggle}
          disabled={busy}
          onClick={() => {
            if (onRequestClose) onRequestClose();
            else setOpen(false);
          }}
        >
          {onRequestClose ? "Закрыть" : "Скрыть"}
        </button>
        {membership.status === "expired" ? (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={busy}
            onClick={() => patch({ status: "active" }, "Абонемент восстановлен")}
          >
            Восстановить
          </Button>
        ) : null}
        <CancelPendingMembershipButton
          membership={membership}
          onSuccess={onChanged}
          size="sm"
        />
      </div>

      {frozen ? (
        <p className={s.clientTabMuted}>
          Пока абонемент заморожен, корректировка недоступна.
        </p>
      ) : null}
    </div>
  );
}
